import styled from "@emotion/styled"
import ImageCLD from "components/media/ImageCLD"
import { useConsole } from "contexts/Console"
import getMediaQuery from "css/breakpoints"
import { body50, headline30, headline50 } from "css/text"
import { motion, useInView } from "framer-motion"
import { Fragment, useRef } from "react"
import BeautyshotButton from "./BeautyshotButton"
import Link from "components/link/Link"

const Root = styled.section`
  background: #fff;

  ${getMediaQuery("l")} {
    display: grid;
  }

  > div {
    display: grid;

    ${getMediaQuery("l")} {
      align-items: center;
      grid-template-columns: 1fr 1fr;
    }

    figure {
      align-items: center;
      display: flex;
    }
  }

  ${getMediaQuery("l")} {
    padding: ${({ padding }) => (padding ? "calc(var(--grid-col-unit) / 2)" : "calc(var(--grid-col-unit) / 2) 0")};
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding-block: 5vh;
  padding-inline: var(--outer-margin);
  row-gap: 2ex;
  text-wrap: pretty;

  ${getMediaQuery("l")} {
    padding-block: 10vh;
  }

  h2 {
    ${headline30}

    &.gradient {
      ${headline50}
      background: ${({ gradient }) => gradient};
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  p {
    ${body50}
  }

  ${getMediaQuery("l", { max: true })} {
    padding-block-end: 10vh;
  }
`

const Figure = styled.figure`
  overflow: hidden;
  position: relative;

  div {
    will-change: transform;
  }

  ${getMediaQuery("l", { max: true })} {
    order: -1;
    padding: var(--outer-margin) var(--outer-margin) 0;
  }

  ${({ beautyshot }) => beautyshot && "cursor: pointer;"};

  * {
    pointer-events: none;
  }

  button {
    pointer-events: auto;
  }
`

const Image = styled(ImageCLD)`
  display: block;
`

const P = styled.p`
  font-weight: 700;
`

const CTA = styled(BeautyshotButton)`
  inset-block-end: 2.5rem;
  inset-inline-end: calc(var(--outer-margin) + 2.5rem);
  position: absolute !important;

  ${getMediaQuery("l")} {
    inset-inline-end: ${({ padding, start_with_image }) =>
      padding ? "calc(var(--outer-margin) - calc(var(--grid-col-unit) / 2))" : start_with_image ? "2.5rem" : "var(--outer-margin)"};
  }
`

const transition = {
  duration: 1.2,
  ease: "easeOut",
  type: "tween",
}

const animation = {
  show: { scale: "100%", transition },
  hide: { scale: "110%", transition },
}

function Text({ title, chapo, paragraph, gradient, cta }) {
  const gradientColors = gradient?.colors?.map(g => `rgb(${g.color} / ${g.opacity})`).join(", ")
  const gradientString = gradientColors && `linear-gradient(${gradient?.angle || "90deg"}, ${gradientColors})`

  return (
    <Content gradient={gradientString}>
      {title && <h2 className={gradientColors ? "gradient" : null} dangerouslySetInnerHTML={{ __html: title }} />}
      {chapo && <P dangerouslySetInnerHTML={{ __html: chapo }} />}
      {paragraph?.map(t => (
        <p key={t} dangerouslySetInnerHTML={{ __html: t }} />
      ))}
      {cta && <Link href={cta.url}>{cta.label}</Link>}
    </Content>
  )
}

export default function TextImageHorizontal(props) {
  const console = useConsole()
  const rroot = useRef()
  const { content, start_with_image, padding, theme } = props

  function onClick(e) {
    if (e.target.tagName === "BUTTON") return
    const button = e.target.getElementsByTagName("button")
    button[0].click()
  }

  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")
  const isInView = useInView(rroot, { margin: "100% 0px 0px 0px" })

  const order = start_with_image ? ["asset", "text"] : ["text", "asset"]

  const beautyshot = content.find(item => item.beautyshot)?.beautyshot
  const gradient = content.find(item => item.gradient)?.gradient
  const cta = content.find(item => item.cta)?.cta

  console.log("TestimoneeTextImageHorizontal(%o)", props)

  return (
    <Root padding={padding} ref={rroot}>
      <div>
        {order.map(type =>
          content.map(({ text, asset }) => {
            if (type === "asset" && asset)
              return (
                <Fragment key={asset._metadata?.uid}>
                  <Figure beautyshot={beautyshot} onClick={onClick} className={theme}>
                    <motion.div initial='hide' animate={!prefersReducedMotion ? (isInView ? "show" : "hide") : "show"} variants={animation}>
                      <Image loading='lazy' sources={asset.asset} alt={asset.alt ?? ""} sizes='(min-width: 64rem) 50vw, 84vw' />
                    </motion.div>
                    {beautyshot && <CTA start_with_image={start_with_image} padding={padding} key={beautyshot._metadata?.uid} beautyshot={beautyshot} />}
                  </Figure>
                </Fragment>
              )
            if (type === "text" && text) return <Text key={text._metadata?.uid} gradient={gradient} cta={cta} {...text} />
            return null
          })
        )}
      </div>
    </Root>
  )
}
